import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/feature/AppShell";
import ProgressBar from "@/components/base/ProgressBar";
import { useTheme, type ThemeMode } from "@/hooks/useTheme";
import { useMotionPreference } from "@/hooks/useMotionPreference";
import { fetchCurrentProfile } from "@/services/auth";
import { fetchMyQuizResult } from "@/services/quiz";
import { useGhlOverview } from "@/hooks/useGhlOverview";
import { useCourseProgress } from "@/hooks/useCourseProgress";
import { computeRouteModules, computeRouteStats } from "@/lib/route";

type ThemeOption = {
  id: ThemeMode;
  label: string;
  desc: string;
  icon: string;
};

const themeOptions: ThemeOption[] = [
  { id: "light", label: "Claro", desc: "Luminoso y limpio para el día.", icon: "ri-sun-line" },
  { id: "dark", label: "Oscuro", desc: "Suave para la vista de noche.", icon: "ri-moon-line" },
  { id: "system", label: "Sistema", desc: "Tono intermedio, gris azulado suave.", icon: "ri-computer-line" },
];

// Muestras fijas de cada tema (no dependen del tema activo).
const PREVIEW: Record<ThemeMode, { bg: string; card: string; accent: string; accent2: string }> = {
  light: { bg: "#F8FAFC", card: "#E5E7EB", accent: "#20C997", accent2: "#7C3AED" },
  dark: { bg: "#14141F", card: "#262638", accent: "#20C997", accent2: "#7C3AED" },
  system: { bg: "#D9E1EC", card: "#C3CFDE", accent: "#20C997", accent2: "#7C3AED" },
};

export default function Apariencia() {
  const navigate = useNavigate();
  const { mode, setMode } = useTheme();
  const { reducedMotion, setReducedMotion } = useMotionPreference();
  const [fullName, setFullName] = useState("Tu perfil");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [profileLabel, setProfileLabel] = useState("Completa tu test");
  const { data: ghl, noContact } = useGhlOverview();
  const { progressByCourseId } = useCourseProgress();
  const modules = useMemo(() => computeRouteModules(progressByCourseId), [progressByCourseId]);
  const { routeProgress } = useMemo(() => computeRouteStats(modules), [modules]);
  const streakDays = !noContact && ghl?.overview.habitsStreak ? Number(ghl.overview.habitsStreak) || 0 : 0;

  useEffect(() => {
    let active = true;
    fetchCurrentProfile().then((profile) => {
      if (!active) return;
      if (profile?.fullName) setFullName(profile.fullName);
      if (profile?.avatarUrl) setAvatarUrl(profile.avatarUrl);
    });
    fetchMyQuizResult()
      .then((result) => {
        if (active && result) setProfileLabel(result.profile.label);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  return (
    <AppShell headerVariant="onLight" showBack>
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Title */}
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-2xl bg-primary-500/15 text-primary-600 flex items-center justify-center">
            <i className="ri-palette-line text-2xl"></i>
          </span>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">Apariencia</h1>
            <p className="text-foreground-600 text-sm">Elige cómo se ve Vívelo. Puedes cambiarlo cuando quieras.</p>
          </div>
        </div>

        {/* Theme options */}
        <h2 className="font-heading font-bold text-foreground-950 mt-8 mb-3">Tema</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {themeOptions.map((opt) => {
            const active = mode === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => setMode(opt.id)}
                aria-pressed={active}
                className={[
                  "relative text-left rounded-2xl border p-4 cursor-pointer transition-all",
                  active
                    ? "border-primary-500 ring-1 ring-primary-500/30 bg-primary-500/10"
                    : "border-background-200 bg-background-100 hover:border-primary-400",
                ].join(" ")}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={[
                      "w-10 h-10 rounded-xl flex items-center justify-center",
                      active ? "bg-primary-500 text-background-950" : "bg-background-200 text-foreground-700",
                    ].join(" ")}
                  >
                    <i className={`${opt.icon} text-xl`}></i>
                  </span>
                  {active ? (
                    <span className="w-6 h-6 rounded-full bg-primary-500 text-background-950 flex items-center justify-center">
                      <i className="ri-check-line text-sm"></i>
                    </span>
                  ) : null}
                </div>
                <div className="mt-3 font-heading font-bold text-foreground-950">{opt.label}</div>
                <div className="text-xs text-foreground-600 mt-0.5">{opt.desc}</div>
                <div className="mt-3 flex items-center gap-1.5" aria-hidden="true">
                  <span className="w-6 h-6 rounded-md border border-black/10" style={{ background: PREVIEW[opt.id].bg }}></span>
                  <span className="w-6 h-6 rounded-md border border-black/10" style={{ background: PREVIEW[opt.id].card }}></span>
                  <span className="w-6 h-6 rounded-md" style={{ background: PREVIEW[opt.id].accent }}></span>
                  <span className="w-6 h-6 rounded-md" style={{ background: PREVIEW[opt.id].accent2 }}></span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-3 rounded-2xl bg-secondary-500/10 border border-secondary-500/25 p-4 flex items-start gap-3">
          <span className="w-9 h-9 rounded-xl bg-secondary-500/20 text-secondary-600 flex items-center justify-center flex-shrink-0">
            <i className="ri-information-line"></i>
          </span>
          <p className="text-sm text-foreground-800">
            El tema <span className="font-semibold text-foreground-950">Sistema</span> es un tono intermedio entre claro y oscuro, en gris azulado suave que descansa la vista.
          </p>
        </div>

        {/* Motion preference */}
        <h2 className="font-heading font-bold text-foreground-950 mt-8 mb-3">Movimiento</h2>
        <button
          type="button"
          role="switch"
          aria-checked={reducedMotion}
          onClick={() => setReducedMotion(!reducedMotion)}
          className={[
            "w-full text-left rounded-2xl border p-4 flex items-center gap-4 cursor-pointer transition-colors",
            reducedMotion
              ? "border-primary-500 ring-1 ring-primary-500/30 bg-primary-500/10"
              : "border-background-200 bg-background-100 hover:border-primary-400",
          ].join(" ")}
        >
          <span
            className={[
              "w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0",
              reducedMotion ? "bg-primary-500 text-background-950" : "bg-background-200 text-foreground-700",
            ].join(" ")}
          >
            <i className="ri-speed-line text-xl"></i>
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-foreground-950">Reducir animaciones</div>
            <div className="text-xs text-foreground-600 mt-0.5">
              Suaviza los movimientos y transiciones de toda la app. Ideal si las animaciones te distraen o te marean.
            </div>
          </div>
          <span
            className={[
              "relative w-12 h-7 rounded-full flex-shrink-0 transition-colors",
              reducedMotion ? "bg-primary-500" : "bg-background-300",
            ].join(" ")}
            aria-hidden="true"
          >
            <span
              className={[
                "absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-background-50 shadow-sm transition-transform",
                reducedMotion ? "translate-x-5" : "translate-x-0",
              ].join(" ")}
            />
          </span>
        </button>
        {reducedMotion ? (
          <div className="mt-3 rounded-2xl bg-secondary-500/10 border border-secondary-500/25 p-4 flex items-start gap-3">
            <span className="w-9 h-9 rounded-xl bg-secondary-500/20 text-secondary-600 flex items-center justify-center flex-shrink-0">
              <i className="ri-checkbox-circle-line"></i>
            </span>
            <p className="text-sm text-foreground-800">
              Movimientos reducidos <span className="font-semibold text-foreground-950">activado</span>. Las animaciones
              aparecerán de forma casi instantánea.
            </p>
          </div>
        ) : null}

        {/* Live preview */}
        <h2 className="font-heading font-bold text-foreground-950 mt-8 mb-3">Vista previa</h2>
        <div className="rounded-2xl border border-background-200 bg-background-100 p-5">
          <div className="flex items-center gap-3">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="Vista previa de perfil"
                className="w-12 h-12 rounded-full object-cover object-top border-2 border-primary-500"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-primary-500/15 border-2 border-primary-500 flex items-center justify-center">
                <i className="ri-user-3-line text-primary-600"></i>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-foreground-950">{fullName}</div>
              <div className="text-xs text-foreground-600">{profileLabel}</div>
            </div>
            <span className="text-[11px] font-semibold bg-accent-500/15 text-accent-700 px-2.5 py-1 rounded-full whitespace-nowrap">
              🔥 {streakDays} días
            </span>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-foreground-600 mb-1">
              <span>Tu ruta</span>
              <span className="font-semibold text-foreground-800">{routeProgress}%</span>
            </div>
            <ProgressBar value={routeProgress} color="primary" />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => navigate("/app/dashboard")}
              className="inline-flex items-center gap-2 bg-primary-500 text-background-950 rounded-full px-4 py-2 font-heading font-bold text-sm hover:bg-primary-600 cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-play-fill"></i> Continuar
            </button>
            <button
              onClick={() => navigate("/app/perfil")}
              className="inline-flex items-center gap-2 border border-background-300 text-foreground-700 rounded-full px-4 py-2 text-sm hover:border-primary-400 cursor-pointer whitespace-nowrap transition-colors"
            >
              Mi perfil
            </button>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-foreground-500">
          Tu preferencia se guarda en este dispositivo.
        </p>
      </div>
    </AppShell>
  );
}